import { Component, inject, OnInit, Optional } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { firstValueFrom, Subscription } from 'rxjs';
import { ArticleEnums } from 'src/app/enum/article-enums';
import { ClassMaintenanceOrderEnum, MaintenanceOrderEnums } from 'src/app/enum/maintenance-order-enum';
import { ActionColor, CheckBoxAction, TableAction } from 'src/app/interfaces/custom-table/custom-table-action.interface';
import { ModalObjectType } from 'src/app/interfaces/modal-object-types.interface';
import { RepairTableModalModel } from 'src/app/models/maintenance/repair-table-modal.model';
import { RepairModel } from 'src/app/models/maintenance/repair.model';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { PagedResponseModel } from 'src/app/models/responses/paged-response.model';
import { ArticleService } from 'src/app/services/archive/article.service';
import { ConfirmDialogService } from 'src/app/services/confirm-dialog/confirm-dialog.service';
import { UserAuthenticatedService } from 'src/app/services/global-services/user-authenticated.service';
import { RepairService } from 'src/app/services/maintenance/repair.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';

@Component({
  selector: 'app-repair-modal',
  templateUrl: './repair-modal.component.html',
  styleUrls: ['./repair-modal.component.scss'],
  standalone: false
})
export class RepairModalComponent implements OnInit {
  private readonly articleService = inject(ArticleService);
  public config = inject(DynamicDialogConfig);
  private readonly formBuilder = inject(FormBuilder);
  private readonly confirmDialogService = inject(ConfirmDialogService);
  private readonly repairService = inject(RepairService);
  @Optional() private ref = inject(DynamicDialogRef);
  private readonly toastHandlerService = inject(ToastHandlerService);
  private readonly userAuthenticatedService = inject(UserAuthenticatedService);

  ascendant: boolean = true;
  changeRepairValue: boolean = false;
  checkboxActions: CheckBoxAction[] = [
    {
      label: 'Eliminar',
      icon: 'pi pi-trash',
      color: ActionColor.DANGER,
      action: 'bulk-delete'
    },
  ];
  clearSelectionTrigger: number = 0;
  columnSort: string | null = null;
  formRepair: FormGroup = new FormGroup({});
  header: ColumnResponseModel[] = [];
  isFormView: boolean = false;
  limit: number = 10;
  listSupply: any[] = [];
  maintenanceOrderId: number = 0;
  maintenanceOrderClassId: number = 0;
  maintenanceOrderStateId: number = 0;
  maintenanceOrderCloseFinal = MaintenanceOrderEnums.cierreFinal;
  repairContract: RepairModel = new RepairModel();
  repairData: PagedResponseModel<RepairTableModalModel> = new PagedResponseModel<RepairTableModalModel>();
  scheduledClass = ClassMaintenanceOrderEnum.programada;
  swBlock: boolean = false;
  swCostRepairState: boolean = false;
  swManagesInventoryOfSupplies: boolean = false;
  page: number = 1;
  permissionUpdatable: boolean = false;
  tableActions: TableAction[] = [
    {
      label: 'Editar',
      icon: 'pi pi-pencil',
      action: 'edit'
    }
  ];
  suscription: Subscription;
  vrTotal: number = 0;

  constructor() {
    this.getGeneralParametersByCompany();
    this.validateForm(this.repairContract);
  }

  async ngOnInit() {
    const input$ = this.config.data?.inputs$;
    if (!input$) {
      console.error('inputs$ no está definido en config.data');
      return;
    }
    this.suscription = input$?.subscribe((inputs: any) => {
      this.maintenanceOrderId = inputs.maintenanceOrderId;
      this.maintenanceOrderClassId = inputs.maintenanceOrderClassId;
      this.permissionUpdatable = inputs.permissionUpdatable;
      this.swCostRepairState = inputs.swCostRepairState;
      this.swBlock = inputs.swBlock;
      this.maintenanceOrderStateId = inputs.maintenanceOrderStateId;
    });

    //No permito modificar la tabla en los siguientes estados
    if (this.permissionUpdatable == false || this.maintenanceOrderStateId === this.maintenanceOrderCloseFinal || this.swCostRepairState || this.swBlock) {
      this.tableActions = [];
    }
    await this.getAllLists();
    await this.getColumns();
    await this.getAllRepairsModalWithPagination();
  }

  ngOnDestroy() {
    this.suscription?.unsubscribe();
  }

  async addSupplies() {
    if (this.swCostRepairState || this.swBlock) {
      this.toastHandlerService.showToast('error', 'No se puede agregar insumos en estado cerrado o con la orden bloqueada.');
      return;
    }
    const canUpdate = await this.validatePermissionUpdatable();
    if (!canUpdate) return;

    const accepted = await this.confirmDialogService.confirm('¿Realmente desea agregar los insumos del plan de mantenimiento?');
    if (accepted) {
      try {
        await firstValueFrom(this.repairService.createRepairsByMaintenanceOrderSuppliesAsync(this.maintenanceOrderId));
        this.toastHandlerService.showToast('success', 'Los insumos fueron agregados exitosamente.');
        this.changeRepairValue = true;
        this.getAllRepairsModalWithPagination();
      } catch (error) {
        console.error('Error al agregar los insumos.', error);
      }
    }
  }

  calculateTotalValue() {
    const quantity = this.formRepair.get('quantity')?.value;
    const unitValue = this.formRepair.get('unitValue')?.value;
    this.vrTotal = quantity * unitValue;
  }

  clearForm() {
    const modelRepair = new RepairModel();
    this.validateForm(modelRepair)
    this.getFormView(false);
  }

  close() {
    const data: ModalObjectType = {};
    if (this.changeRepairValue) {
      data.params = { reload: true };
      this.ref.close(data);
    } else {
      data.params = { reload: false };
      this.ref.close(data);
    }
  }

  async deleteRepair(selectedIds: number[]) {
    if (selectedIds.length === 0) return;

    const confirmation = await this.confirmDialogService.confirm('¿Realmente desea eliminar la reparación?')
    if (confirmation) {
      try {
        await firstValueFrom(this.repairService.deleteSomeRepairs(selectedIds));
        this.toastHandlerService.showToast('success', 'La reparación fue eliminada exitosamente.', 'Atención');
      } catch (error) {
        console.error('Error al eliminar la reparación de la orden de mantenimiento .', error);
      }
    };
  }

  async getAllLists() {
    try {
      const [supplies] = await Promise.all([
        firstValueFrom(this.articleService.getAllSuppliesForSelectAsync()),
      ]);
      this.listSupply = supplies

      return true;
    } catch (error) {
      console.error('Error al obtener las listas.', error);
      return false;
    }
  }

  async getColumns() {
    try {
      this.header = await firstValueFrom(this.repairService.getColumnsForModalConsultation());
    } catch (error) {
      console.error('Error al buscar las columnas para la consulta.', error);
    }
  }

  async getAllRepairsModalWithPagination() {
    try {
      this.repairData = await firstValueFrom(
        this.repairService.getRepairsModalWithPagination(this.page, this.limit, this.columnSort, this.ascendant, this.maintenanceOrderId));
    } catch (error) {
      console.error('Error al obtener las Reparaciones.', error);
    }
  }

  getFormView(event: boolean) {
    this.isFormView = event;
  }

  getGeneralParametersByCompany() {
    return new Promise((resolve) => {
      this.userAuthenticatedService.generalParameterObservable.subscribe(response => {
        this.swManagesInventoryOfSupplies = response.some(x => x.name === "ManejaInventarioDeInsumos");
      });
      resolve(true);
    });
  }

  async newRepair(button: boolean) {
    if (this.swCostRepairState || this.swBlock) {
      this.toastHandlerService.showToast('error', 'No se puede agregar una reparación en estado cerrado o con la orden bloqueada.');
      return;
    }
    const canUpdate = await this.validatePermissionUpdatable();
    if (!canUpdate) return;
    this.repairContract = new RepairModel();
    this.repairContract.maintenanceOrderId = this.maintenanceOrderId;
    this.validateForm(this.repairContract);
    if (button) this.getFormView(true);
  }

  async onAction(event: { action: string, item?: RepairTableModalModel, selectedItems?: number[] }) {

    if (this.swManagesInventoryOfSupplies && event.item?.typeArticleId === ArticleEnums.tipoInsumo) {
      this.toastHandlerService.showToast(
        'warning', 'No se pueden modificar los insumos agregados por un pedido.'
      );
      return;
    }
    if (event.action === 'edit' && event.item) {
      if (this.swCostRepairState || this.swBlock) {
        this.toastHandlerService.showToast('error', 'No se puede editar una reparación en estado cerrado o con la orden bloqueada.');
        return;
      }
      const canUpdate = await this.validatePermissionUpdatable();
      if (!canUpdate) return;
      try {
        this.repairContract = await firstValueFrom(this.repairService.getRepairById(event.item.id));
        this.validateForm(this.repairContract);
        this.getFormView(true);
      } catch (error) {
        console.error('Error al consultar la Reparacion por Id.', error);
      }
    }
    if (event.action === 'bulk-delete' && event.selectedItems) {
      if (this.swCostRepairState || this.swBlock) {
        this.toastHandlerService.showToast('error', 'No se puede eliminar una reparación en estado cerrado o con la orden bloqueada.');
        return;
      }

      const dataSelected = this.repairData.items.filter(x => event.selectedItems?.includes(x.id));
      if (this.swManagesInventoryOfSupplies && dataSelected.some(x => x.typeArticleId === ArticleEnums.tipoInsumo)) {
        this.toastHandlerService.showToast('warning', 'No se pueden eliminar los insumos agregados por un pedido.');
        return;
      }

      const canUpdate = await this.validatePermissionUpdatable();
      if (!canUpdate) return;

      this.changeRepairValue = true;
      await this.deleteRepair(event.selectedItems);
      this.getAllRepairsModalWithPagination();
      this.clearSelectionTrigger++;
      this.repairContract = new RepairModel();
      this.getFormView(false);
    }
  }

  async onExport() {
    try {
      const response = await firstValueFrom(this.repairService.exportModalConsultation(this.maintenanceOrderId, this.columnSort, this.ascendant));
      const blob = response;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'Reparaciones.xlsx';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error al exportar las reparaciones.', error);
    }
  }

  async onPageChange(event: { page: number; pageSize: number, sortField: string | undefined, sortOrder: number }) {
    this.page = event.page;
    this.limit = event.pageSize;
    this.columnSort = event.sortField || null;
    this.ascendant = event.sortOrder === 1;
    this.getAllRepairsModalWithPagination();
  }

  async send() {
    const canUpdate = await this.validatePermissionUpdatable();
    if (!canUpdate) return;
    Object.keys(this.formRepair.controls).forEach(key => {
      const control = this.formRepair.get(key);
      if (control && control.invalid) {
        console.warn(`${key} es inválido`, control.errors, control.value);
      }
    });
    if (this.formRepair.valid) {
      //Habilito el formulario para poder obtener los datos
      this.formRepair.enable();
      const modelRepair = this.formRepair.value as RepairModel;
      //Hago null, los campos que permiten ser nulos:
      modelRepair.devolutionDetailId = null;
      //Valido si se va a crear o actualizar la Reparación
      if (this.repairContract.repairId === 0) {
        //Asigno los valores en las variables fuera del formulario
        modelRepair.maintenanceOrderId = this.maintenanceOrderId;
        try {
          this.repairContract = await firstValueFrom(this.repairService.createRepair(modelRepair));
          this.validateForm(this.repairContract);
          this.toastHandlerService.showToast('success', 'La Reparación fue creada exitosamente.');
        } catch (error) {
          console.error('Error al crear la Reparación.', error);
        }
      } else {
        this.repairContract.articleId = modelRepair.articleId;
        this.repairContract.detailText = modelRepair.detailText;
        this.repairContract.quantity = modelRepair.quantity;
        this.repairContract.unitValue = modelRepair.unitValue;
        try {
          this.repairContract = await firstValueFrom(this.repairService.updateRepair(this.repairContract));
          this.toastHandlerService.showToast('success', 'La Reparación fue modificada exitosamente.');
        } catch (error) {
          console.error('Error al actualizar la Reparación.', error);
        }
      }
      this.changeRepairValue = true;
      this.getAllRepairsModalWithPagination();
      this.newRepair(false);
    } else {
      this.showValidationMessages();
    }
  }

  showValidationMessages() {
    const controlMessages: any = {
      articleId: 'El campo Insumo es obligatorio.',
      quantity: 'El campo Cantidad es obligatorio.',
      unitValue: 'El campo Vr.Unit. es obligatorio.',
    };
    Object.keys(controlMessages).forEach(control => {
      if (this.formRepair.controls[control].invalid) {
        this.toastHandlerService.showToast('warning', controlMessages[control], 'Atención');
      }
    });
  }

  validateForm(repair: RepairModel) {
    this.formRepair = this.formBuilder.group({
      articleId: [repair.articleId, [Validators.required, Validators.min(1)]],
      detailText: [repair.detailText, [Validators.maxLength(70)]],
      quantity: [repair.quantity, [Validators.required, Validators.min(0), Validators.max(9999999.99)]],
      unitValue: [repair.unitValue, [Validators.required, Validators.min(0), Validators.max(99999999999.99)]]
    });
    this.calculateTotalValue();
  }

  async validatePermissionUpdatable(): Promise<boolean> {
    if (!this.permissionUpdatable) {
      this.toastHandlerService.showToast('error', 'El usuario no tiene permisos para modificar Órdenes de Mantenimiento. Comuníquese con el administrador del sistema.', 'Error');
      return false;
    }
    return true;
  }

}
